/**
 * Gallery — a live grid rather than a table: each tile takes the span it will
 * take on the public masonry grid, so an editor sees the layout they are
 * building. The span selector on a tile saves straight away.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Pencil, Plus, Trash2, Video } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { mediaPreviewUrl } from "@/lib/media";
import { GALLERY, GALLERY_CATEGORIES, GALLERY_SPANS } from "@/lib/deptContent";
import { useDepartments } from "@/hooks/useDepartments";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/_app/gallery/")({ component: GalleryPage });

interface GalleryRow {
  id: number;
  image_r2_key: string;
  alt: string | null;
  caption: string | null;
  category: string;
  dept: string | null;
  span: "1x1" | "2x1" | "1x2" | "2x2";
  video_r2_key: string | null;
  sort_order: number;
  status: "draft" | "published";
}

const SPAN_CLASS: Record<GalleryRow["span"], string> = {
  "1x1": "col-span-1 row-span-1",
  "2x1": "col-span-2 row-span-1",
  "1x2": "col-span-1 row-span-2",
  "2x2": "col-span-2 row-span-2",
};

const ALL = "__all__";
const INSTITUTION = "__institution__";

function GalleryPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { options: deptOptions, scoped } = useDepartments();
  const [category, setCategory] = useState<string>(ALL);
  const [dept, setDept] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);

  const { data, isLoading } = useQuery({
    queryKey: [GALLERY.key, "list"],
    queryFn: () => fetchAll<GalleryRow>(GALLERY.apiPath),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: [GALLERY.key] });
  const onError = (e: unknown) => toast.error(e instanceof Error ? e.message : "Request failed");

  const patchSpan = useMutation({
    mutationFn: ({ id, span }: { id: number; span: string }) => api.patch(`${GALLERY.apiPath}/${id}`, { span }),
    onSuccess: () => { toast.success("Tile size saved"); invalidate(); },
    onError,
  });
  const setPublished = useMutation({
    mutationFn: ({ id, publish }: { id: number; publish: boolean }) =>
      api.post(`${GALLERY.apiPath}/${id}/${publish ? "publish" : "unpublish"}`),
    onSuccess: (_d, v) => { toast.success(v.publish ? "Published" : "Unpublished"); invalidate(); },
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`${GALLERY.apiPath}/${id}`),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError,
  });

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    return items
      .filter((r) => category === ALL || r.category === category)
      .filter((r) => dept === ALL || (dept === INSTITUTION ? !r.dept : r.dept === dept))
      .filter((r) => status === ALL || r.status === status)
      .sort((a, b) => a.sort_order - b.sort_order || b.id - a.id);
  }, [data, category, dept, status]);

  const canEdit = can(GALLERY.resource, "update");
  const canPublish = can(GALLERY.resource, "publish");

  return (
    <div className="space-y-4">
      <PageHeader
        title={GALLERY.title}
        description={GALLERY.description}
        actions={can(GALLERY.resource, "create") && (
          <Link to="/gallery/new"><Button><Plus className="h-4 w-4" /> New photo</Button></Link>
        )}
      />

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Category" value={category} onChange={setCategory}
          options={[{ value: ALL, label: "All categories" }, ...GALLERY_CATEGORIES]} />
        <FilterSelect label="Department" value={dept} onChange={setDept}
          options={[
            { value: ALL, label: "All departments" },
            ...(scoped ? [] : [{ value: INSTITUTION, label: "Institution-wide" }]),
            ...deptOptions.map((d) => ({ value: d.value, label: d.label })),
          ]} />
        <FilterSelect label="Status" value={status} onChange={setStatus}
          options={[{ value: ALL, label: "Any status" }, { value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
        <span className="ml-auto text-xs text-muted-foreground">
          {isLoading ? "Loading…" : `${rows.length} of ${data?.items.length ?? 0} shown`}
        </span>
      </div>

      {!isLoading && rows.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground">
          No photos match these filters.
        </div>
      ) : (
        <div className="grid grid-flow-row-dense auto-rows-[150px] grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
          {rows.map((r) => {
            const src = mediaPreviewUrl(r.image_r2_key);
            return (
              <figure key={r.id} className={cn("group relative overflow-hidden rounded-md border bg-secondary", SPAN_CLASS[r.span] ?? SPAN_CLASS["1x1"])}>
                {src && <img src={src} alt={r.alt ?? ""} loading="lazy" className="h-full w-full object-cover" />}
                <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-1 p-1.5">
                  <div className="flex flex-wrap gap-1">
                    <StatusBadge status={r.status} />
                    {r.video_r2_key && <Badge variant="secondary"><Video className="h-3 w-3" /></Badge>}
                  </div>
                  {canEdit && (
                    <Select value={r.span} onValueChange={(span) => patchSpan.mutate({ id: r.id, span })}>
                      <SelectTrigger className="h-7 w-[78px] bg-background/90 px-2 text-xs" title="Tile size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GALLERY_SPANS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2 text-white">
                  <p className="truncate text-xs font-medium">{r.caption || r.alt || "Untitled"}</p>
                  <p className="text-[10px] uppercase tracking-wider opacity-80">
                    {r.category}{r.dept ? ` · ${r.dept}` : " · institution"}
                  </p>
                  <div className="mt-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <Link to="/gallery/$id" params={{ id: String(r.id) }}>
                      <Button size="sm" variant="secondary" className="h-7 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
                    </Link>
                    {canPublish && (
                      <Button size="sm" variant="secondary" className="h-7 px-2"
                        title={r.status === "published" ? "Unpublish" : "Publish"}
                        onClick={() => setPublished.mutate({ id: r.id, publish: r.status !== "published" })}>
                        {r.status === "published" ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {can(GALLERY.resource, "delete") && (
                      <Button size="sm" variant="secondary" className="h-7 px-2 text-destructive" title="Delete"
                        onClick={() => { if (confirm("Delete this photo?")) remove.mutate(r.id); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: readonly { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[190px]" aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
